import { useEffect, useState } from "react";
import { TICKS_PER_SECOND } from "@empire/core-sim";
import type { ReportRow } from "@empire/shared";
import { LootLine } from "../components/LootLine.js";
import { fetchReports } from "../../api/attack.js";
import { useReplayStore } from "../../store/replay.js";
import { useSessionStore } from "../../store/session.js";
import { useTutorialStore } from "../../store/tutorial.js";

/**
 * Every battle you have been in, either end of it (S5-4 client side).
 *
 * Both sides of a battle get a row, and they are not the same row: the
 * defender's says who attacked them, the attacker's says who they attacked.
 * Tapping either opens the replay — which is where the two viewers diverge
 * sharply, because only one of them is allowed to see the maze.
 */
export function Reports(): JSX.Element {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionStatus = useSessionStore((s) => s.status);
  const open = useReplayStore((s) => s.open);

  useEffect(() => {
    if (sessionStatus !== "ready") return;
    let cancelled = false;

    void (async () => {
      setLoading(true);
      setError(null);
      const token = await useSessionStore.getState().token();
      if (!token || cancelled) return;
      try {
        const body = await fetchReports(token);
        if (!cancelled) setReports(body.reports);
      } catch {
        if (!cancelled) setError("Couldn't load your reports.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionStatus]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-3 border-b border-panel-edge bg-bg-raised px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">Reports</h2>
          <p className="text-[11px] text-ink-dim">
            Every battle you have been in, either end of it.
          </p>
        </div>
        {/* The way back into the first ten minutes. Before this existed, one
            accidental tap on Skip removed the tutorial from somebody's account
            permanently — there was no way to clear the flag from inside the
            app, which also made it impossible to test. */}
        <button
          type="button"
          data-testid="replay-tutorial"
          onClick={() => useTutorialStore.getState().restart()}
          className="ml-auto h-11 shrink-0 rounded-lg border border-panel-edge bg-panel px-3 text-[11px] font-semibold text-ink-dim hover:text-ink"
        >
          Replay tutorial
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {loading ? <p className="text-[11px] text-ink-dim">Loading…</p> : null}
        {error ? (
          <p className="rounded-lg border border-warn/30 bg-warn/10 px-3 py-2 text-[11px] text-warn">
            {error}
          </p>
        ) : null}

        {!loading && !error && reports.length === 0 ? (
          <div className="rounded-xl border border-panel-edge bg-panel px-4 py-6 text-center">
            <p className="text-sm text-ink">Nothing has happened yet.</p>
            <p className="mt-1 text-[11px] text-ink-dim">
              Attack someone, or wait for somebody to attack you.
            </p>
          </div>
        ) : null}

        <ul className="flex flex-col gap-2" data-testid="reports">
          {reports.map((report) => (
            <li key={report.id}>
              <button
                type="button"
                data-testid={`report-${report.id}`}
                disabled={!report.battleId}
                onClick={() =>
                  report.battleId &&
                  void open(report.battleId, report.kind === "battle_att" ? "attacker" : "defender")
                }
                className="w-full rounded-xl border border-panel-edge bg-panel px-3 py-3 text-left enabled:hover:border-gold disabled:opacity-60"
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm font-semibold text-ink">{report.title}</span>
                  <span
                    className={`shrink-0 text-[10px] font-bold uppercase ${verdictColour(report)}`}
                  >
                    {verdict(report)}
                  </span>
                </span>
                <span className="mt-1 block text-[11px] tabular-nums text-ink-dim">
                  {report.summary.army.reached}/{report.summary.army.sent} reached ·{" "}
                  {report.summary.army.lost} lost ·{" "}
                  {(report.summary.ticks / TICKS_PER_SECOND).toFixed(1)}s
                </span>
                <span className="mt-0.5 block">
                  <LootLine
                    loot={report.summary.loot}
                    note={report.summary.lootNote}
                    economics={report.summary}
                    side={report.kind === "battle_att" ? "attacker" : "defender"}
                  />
                </span>
                <span className="mt-0.5 block text-[10px] text-ink-dim opacity-70">
                  {new Date(report.createdAt).toLocaleString()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * The same battle reads as a win to one side and a loss to the other.
 *
 * A breach is good news for the attacker and bad news for the defender, so the
 * word depends on which report this is — showing "Breached" in green to
 * somebody whose village was just sacked would be a small insult.
 */
function verdict(report: ReportRow): string {
  const breached = report.summary.outcome === "breach";
  if (report.kind === "battle_att") return breached ? "broke through" : "repelled";
  return breached ? "breached" : "held";
}

function verdictColour(report: ReportRow): string {
  const breached = report.summary.outcome === "breach";
  const goodForMe = report.kind === "battle_att" ? breached : !breached;
  return goodForMe ? "text-ok" : "text-danger";
}
